package com.yet.tetris.database

import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import com.yet.tetris.domain.model.game.Difficulty
import kotlin.Any
import kotlin.Long
import kotlin.String

public class GameHistoryQueries(
  driver: SqlDriver,
  private val GameHistoryAdapter: GameHistory.Adapter,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> getAllGames(mapper: (
    id: String,
    score: Long,
    linesCleared: Long,
    level: Long,
    difficulty: Difficulty,
    timestamp: Long,
    durationMs: Long,
    piecesPlaced: Long,
    maxCombo: Long,
    tetrisesCleared: Long,
    tSpinClears: Long,
    perfectClears: Long,
    hardDrops: Long,
    hardDropCells: Long,
    softDropCells: Long,
  ) -> T): Query<T> = Query(431_516_195, arrayOf("GameHistory"), driver, "GameHistory.sq", "getAllGames", """
  |SELECT GameHistory.id, GameHistory.score, GameHistory.linesCleared, GameHistory.level, GameHistory.difficulty, GameHistory.timestamp, GameHistory.durationMs, GameHistory.piecesPlaced, GameHistory.maxCombo, GameHistory.tetrisesCleared, GameHistory.tSpinClears, GameHistory.perfectClears, GameHistory.hardDrops, GameHistory.hardDropCells, GameHistory.softDropCells FROM GameHistory
  |ORDER BY timestamp DESC
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getLong(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      GameHistoryAdapter.difficultyAdapter.decode(cursor.getString(4)!!),
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getLong(11)!!,
      cursor.getLong(12)!!,
      cursor.getLong(13)!!,
      cursor.getLong(14)!!
    )
  }

  public fun getAllGames(): Query<GameHistory> = getAllGames(::GameHistory)

  public fun <T : Any> getGameById(id: String, mapper: (
    id: String,
    score: Long,
    linesCleared: Long,
    level: Long,
    difficulty: Difficulty,
    timestamp: Long,
    durationMs: Long,
    piecesPlaced: Long,
    maxCombo: Long,
    tetrisesCleared: Long,
    tSpinClears: Long,
    perfectClears: Long,
    hardDrops: Long,
    hardDropCells: Long,
    softDropCells: Long,
  ) -> T): Query<T> = GetGameByIdQuery(id) { cursor ->
    mapper(
      cursor.getString(0)!!,
      cursor.getLong(1)!!,
      cursor.getLong(2)!!,
      cursor.getLong(3)!!,
      GameHistoryAdapter.difficultyAdapter.decode(cursor.getString(4)!!),
      cursor.getLong(5)!!,
      cursor.getLong(6)!!,
      cursor.getLong(7)!!,
      cursor.getLong(8)!!,
      cursor.getLong(9)!!,
      cursor.getLong(10)!!,
      cursor.getLong(11)!!,
      cursor.getLong(12)!!,
      cursor.getLong(13)!!,
      cursor.getLong(14)!!
    )
  }

  public fun getGameById(id: String): Query<GameHistory> = getGameById(id, ::GameHistory)

  public fun getGamesCount(): Query<Long> = Query(-1_621_725_551, arrayOf("GameHistory"), driver, "GameHistory.sq", "getGamesCount", "SELECT COUNT(*) FROM GameHistory") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertGame(
    id: String,
    score: Long,
    linesCleared: Long,
    level: Long,
    difficulty: Difficulty,
    timestamp: Long,
    durationMs: Long,
    piecesPlaced: Long,
    maxCombo: Long,
    tetrisesCleared: Long,
    tSpinClears: Long,
    perfectClears: Long,
    hardDrops: Long,
    hardDropCells: Long,
    softDropCells: Long,
  ): Long {
    val result = driver.execute(-807_181_922, """
        |INSERT INTO GameHistory (
        |    id, score, linesCleared, level, difficulty, timestamp, durationMs, piecesPlaced,
        |    maxCombo, tetrisesCleared, tSpinClears, perfectClears, hardDrops, hardDropCells, softDropCells
        |)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 15) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
          bindLong(parameterIndex++, score)
          bindLong(parameterIndex++, linesCleared)
          bindLong(parameterIndex++, level)
          bindString(parameterIndex++, GameHistoryAdapter.difficultyAdapter.encode(difficulty))
          bindLong(parameterIndex++, timestamp)
          bindLong(parameterIndex++, durationMs)
          bindLong(parameterIndex++, piecesPlaced)
          bindLong(parameterIndex++, maxCombo)
          bindLong(parameterIndex++, tetrisesCleared)
          bindLong(parameterIndex++, tSpinClears)
          bindLong(parameterIndex++, perfectClears)
          bindLong(parameterIndex++, hardDrops)
          bindLong(parameterIndex++, hardDropCells)
          bindLong(parameterIndex++, softDropCells)
        }.await()
    notifyQueries(-807_181_922) { emit ->
      emit("GameHistory")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteGame(id: String): Long {
    val result = driver.execute(-8_758_384, """
        |DELETE FROM GameHistory
        |WHERE id = ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, id)
        }.await()
    notifyQueries(-8_758_384) { emit ->
      emit("GameHistory")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun clearAllGames(): Long {
    val result = driver.execute(-1_785_193_766, """DELETE FROM GameHistory""", 0).await()
    notifyQueries(-1_785_193_766) { emit ->
      emit("GameHistory")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteOldestGames(`value`: Long): Long {
    val result = driver.execute(2_050_823_844, """
        |DELETE FROM GameHistory
        |WHERE id IN (
        |    SELECT id FROM GameHistory
        |    ORDER BY timestamp ASC
        |    LIMIT ?
        |)
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, value)
        }.await()
    notifyQueries(2_050_823_844) { emit ->
      emit("GameHistory")
    }
    return result
  }

  private inner class GetGameByIdQuery<out T : Any>(
    public val id: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("GameHistory", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("GameHistory", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_181_259_623, """
    |SELECT GameHistory.id, GameHistory.score, GameHistory.linesCleared, GameHistory.level, GameHistory.difficulty, GameHistory.timestamp, GameHistory.durationMs, GameHistory.piecesPlaced, GameHistory.maxCombo, GameHistory.tetrisesCleared, GameHistory.tSpinClears, GameHistory.perfectClears, GameHistory.hardDrops, GameHistory.hardDropCells, GameHistory.softDropCells FROM GameHistory
    |WHERE id = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, id)
    }

    override fun toString(): String = "GameHistory.sq:getGameById"
  }
}
